import { useState } from 'react';
import { useT } from '@/i18n';
import type { Player } from '@/domain/types';
import './AuthScreen.css';

interface MigrationScreenProps {
  localPlayer: Player;
  onMigrate: () => Promise<void>;
  onDiscard: () => Promise<void>;
}

export function MigrationScreen({ localPlayer, onMigrate, onDiscard }: MigrationScreenProps) {
  const t = useT();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const totalEquipment =
    Object.values(localPlayer.equipment).filter(Boolean).length +
    localPlayer.equipmentStash.length;
  const totalMiners = Object.values(localPlayer.autoMiners).reduce((a, b) => a + b, 0);

  const handle = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (err) {
      setError((err as Error).message ?? 'Fehler');
      setBusy(false);
    }
  };

  return (
    <div className="auth-screen">
      <div className="auth-screen__bg" aria-hidden />
      <div className="auth-screen__content">
        <h1 className="auth-screen__title">Lokaler Runner gefunden</h1>
        <p className="auth-screen__tagline">Übernehmen oder neu starten?</p>

        <div className="migration-card">
          <div className="migration-card__row">
            <span className="migration-card__label">Name</span>
            <span className="migration-card__val">{localPlayer.name}</span>
          </div>
          <div className="migration-card__row">
            <span className="migration-card__label">Level</span>
            <span className="migration-card__val">{localPlayer.level} (XP {localPlayer.xp})</span>
          </div>
          <div className="migration-card__row">
            <span className="migration-card__label">{t.hud.credits}</span>
            <span className="migration-card__val">{localPlayer.credits.toLocaleString('de-DE')} ◈</span>
          </div>
          <div className="migration-card__row">
            <span className="migration-card__label">Equipment</span>
            <span className="migration-card__val">{totalEquipment} Items</span>
          </div>
          <div className="migration-card__row">
            <span className="migration-card__label">Auto-Miner</span>
            <span className="migration-card__val">{totalMiners} aktiv</span>
          </div>
        </div>

        {error && <div className="auth-screen__error">⛔ {error}</div>}

        <button
          className="btn btn--primary auth-screen__submit"
          onClick={() => handle(onMigrate)}
          disabled={busy}
        >
          {busy ? 'Übertrage …' : '☁ In Account übernehmen'}
        </button>

        <button
          className="btn btn--ghost"
          onClick={() => handle(onDiscard)}
          disabled={busy}
        >
          🆕 Neu anfangen (lokaler Save wird gelöscht)
        </button>
      </div>
    </div>
  );
}
